import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { logClientAudit } from '../lib/audit'
import type { Site } from '../types'

export function useSites(orgId?: string | null) {
  const [sites, setSites] = useState<Site[]>([])
  const [cameraCounts, setCameraCounts] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    let query = supabase
      .from('sites')
      .select('*, cameras(count)')
      .order('name')

    // If orgId is provided, filter to that org; otherwise RLS handles it
    if (orgId) {
      query = query.eq('org_id', orgId)
    }

    const { data } = await query
    const rows = (data ?? []) as (Site & { cameras: { count: number }[] })[]
    const counts: Record<string, number> = {}
    for (const row of rows) {
      counts[row.id] = row.cameras?.[0]?.count ?? 0
    }
    setSites(rows)
    setCameraCounts(counts)
    setLoading(false)
  }, [orgId])

  useEffect(() => { load() }, [load])

  const createSite = useCallback(async (site: {
    org_id: string
    name: string
    group_name?: string
  }) => {
    const { data, error } = await supabase
      .from('sites')
      .insert(site)
      .select()
      .single()
    if (error) throw error
    logClientAudit({ action: 'create', entity_type: 'site', entity_id: (data as Site).id, entity_name: site.name })
    await load()
    return data as Site
  }, [load])

  const updateSite = useCallback(async (id: string, updates: Partial<Pick<Site, 'name' | 'group_name' | 'latitude' | 'longitude'>>) => {
    const { error } = await supabase
      .from('sites')
      .update(updates)
      .eq('id', id)
    if (error) throw error
    logClientAudit({ action: 'update', entity_type: 'site', entity_id: id, details: updates })
    await load()
  }, [load])

  const deleteSite = useCallback(async (id: string) => {
    const site = sites.find(s => s.id === id)
    const { error } = await supabase
      .from('sites')
      .delete()
      .eq('id', id)
    if (error) throw error
    logClientAudit({ action: 'delete', entity_type: 'site', entity_id: id, entity_name: site?.name })
    await load()
  }, [load, sites])

  return { sites, cameraCounts, loading, reload: load, createSite, updateSite, deleteSite }
}
